import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import { Person, PersonAdd, Add, Edit, Delete, Search, Refresh, DeleteForever } from '@mui/icons-material';
import HeaderBar from '../../../components/header';
import UserRegistrationForm from '../../../components/userRegistration';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { styles } from './styles';
import { darkModeTextField } from '../../../styles/textFieldStyles';
import { darkModeSelect } from '../../../styles/selectStyles';

// Interface para o usuário
interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  isRegistrationComplete: boolean;
}

// Interface para os dados de edição
interface EditUserData {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
}

const UserManagementPage: React.FC = () => {
const [activeTab, setActiveTab] = useState(0);
const { user } = useAuth();
const navigate = useNavigate();
  
  // Estados para listagem e edição de usuários
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editUserData, setEditUserData] = useState<EditUserData>({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    isActive: true
  });
  
  // Buscar a lista de usuários
  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Usando a rota correta /api/users em vez de /api/admin/users
      const response = await api.get('/users');
      const data = response.data.data || response.data;
      setUsers(data);
    } catch (err: any) {
      console.error('Erro ao buscar usuários:', err);
      setError(err.response?.data?.message || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  // Carregar usuários quando a página for montada
  useEffect(() => {
    // Verificar permissões para acessar a página
    if (user) {
      if (user.role === 'Admin' || user.role === 'Developer' || 
          user.role === 'admin' || user.role === 'developer') {
        fetchUsers();
      } else {
        navigate('/unauthorized');
      }
    } else if (!loading) {
      // Se o usuário não estiver carregando e não estiver autenticado, redirecionar para login
      navigate('/login');
    }
  }, [user, navigate]);
  
  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };
  
  // Verificar se o usuário atual é administrador ou desenvolvedor
  const isAdminOrDeveloper = user && (user.role === 'Admin' || user.role === 'Developer' || 
                               user.role === 'admin' || user.role === 'developer');
                               
  // Abrir o dialog de edição
  const handleEditUser = (user: User) => {
    // Verificar permissões
    if (!isAdminOrDeveloper) {
      setError('Você não tem permissão para editar usuários');
      return;
    }
    
    setSelectedUser(user);
    setEditUserData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      role: user.role || 'Default',
      isActive: user.isActive !== undefined ? user.isActive : true
    });
    setEditDialogOpen(true);
  };
  
  // Fechar o dialog de edição
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
  };
  
  // Abrir o dialog de confirmação de exclusão
  const handleDeleteUser = (userToDelete: User) => {
    // Verificar permissões
    if (!isAdminOrDeveloper) {
      setError('Você não tem permissão para excluir usuários');
      return;
    }
    
    // Verificar se está tentando excluir o próprio usuário
    if (user && userToDelete.id === user.id) {
      setError('Não é possível excluir o próprio usuário');
      return;
    }
    
    setSelectedUser(userToDelete);
    setDeleteDialogOpen(true);
  };
  
  // Fechar o dialog de confirmação de exclusão
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };
  
  // Confirmar exclusão do usuário
  const handleConfirmDeleteUser = async () => {
    if (!selectedUser) return;
    
    // Verificar permissões
    if (!isAdminOrDeveloper) {
      setError('Você não tem permissão para excluir usuários');
      return;
    }
    
    setLoading(true);
    try {
      await api.delete(`/users/${selectedUser.id}`);
      
      // Atualizar a lista de usuários
      setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
      
      setSuccess('Usuário excluído com sucesso!');
      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('Erro ao excluir usuário:', err);
      setError(err.response?.data?.message || 'Erro ao excluir usuário');
    } finally {
      setLoading(false);
    }
  };
  
  // Atualizar os dados de edição quando os campos mudam
  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handler específico para os selects
  const handleSelectChange = (e: SelectChangeEvent<string | boolean>) => {
    const { name, value } = e.target;
    setEditUserData(prev => ({
      ...prev,
      [name]: name === 'isActive' ? value === 'true' : value
    }));
  };
  
  // Salvar as alterações do usuário
  const handleSaveUser = async () => {
    if (!selectedUser) return;
    
    // Verificar permissões
    if (!isAdminOrDeveloper) {
      setError('Você não tem permissão para editar usuários');
      return;
    }
    
    setLoading(true);
    try {
      // Usa a rota correta /api/users/{id} em vez de /api/admin/users/{id}
      const response = await api.put(`/users/${selectedUser.id}`, editUserData);
      
      // Atualizar a lista de usuários
      setUsers(prev => prev.map(u => 
        u.id === selectedUser.id ? { ...u, ...editUserData } : u
      ));
      
      setSuccess('Usuário atualizado com sucesso!');
      handleCloseEditDialog();
    } catch (err: any) {
      console.error('Erro ao atualizar usuário:', err);
      setError(err.response?.data?.message || 'Erro ao atualizar usuário');
    } finally {
      setLoading(false);
    }
  };
  
  // Resetar mensagens de sucesso/erro
  const handleCloseAlert = () => {
    setError(null);
    setSuccess(null);
  };
  
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0d1828' }}>
      <HeaderBar 
        title="Gerenciamento de Usuários"
        showBackButton={true}
        subtitle="Registre novos usuários no sistema. Apenas administradores e desenvolvedores podem acessar esta página."
      />
      
      {/* Add spacing to account for the fixed HeaderBar */}
      <Box sx={{ height: '64px' }} />

      <Container maxWidth="lg" sx={styles.container}>
        {/* Tabs no estilo da página Hub */}
        <Box sx={styles.tabsContainer}>
          <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', mb: 3 }}>
            <Button
              sx={{
                ...styles.tabButton,
                color: activeTab === 0 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                borderBottom: activeTab === 0 ? '2px solid #2196f3' : 'none',
                fontWeight: activeTab === 0 ? 600 : 400,
              }}
              onClick={() => handleTabChange(0)}
            >
              Registrar Usuário
            </Button>
            <Button
              sx={{
                ...styles.tabButton,
                color: activeTab === 1 ? 'white' : 'rgba(255, 255, 255, 0.6)',
                borderBottom: activeTab === 1 ? '2px solid #2196f3' : 'none',
                fontWeight: activeTab === 1 ? 600 : 400,
              }}
              onClick={() => handleTabChange(1)}
            >
              Listar Usuários
            </Button>
          </Box>
        </Box>
        
        {/* Painel de Registro de Usuário */}
        <Box
          sx={{ display: activeTab === 0 ? 'block' : 'none' }}
        >
          <UserRegistrationForm onUserRegistered={() => {
            setSuccess('Usuário registrado com sucesso!');
            fetchUsers(); // Atualizar a lista de usuários após o registro
          }} />
        </Box>
        
        {/* Painel de Listagem de Usuários */}
        <Box
          sx={{ display: activeTab === 1 ? 'block' : 'none' }}
        >
          <Paper sx={styles.tableContainer}>
            {/* Cabeçalho da tabela com ações */}
            <Box sx={styles.tableHeader}>
              <Typography variant="h6">Usuários do Sistema</Typography>
              <Button
                startIcon={<Refresh />}
                variant="outlined"
                onClick={fetchUsers}
                size="small"
              >
                Atualizar
              </Button>
            </Box>
            
            {/* Tabela de usuários */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Username</TableCell>
                      <TableCell>Nome</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Função</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.length > 0 ? (
                      users.map((rowUser) => (
                        <TableRow key={rowUser.id}>
                          <TableCell>{rowUser.username}</TableCell>
                          <TableCell>{`${rowUser.firstName} ${rowUser.lastName}`}</TableCell>
                          <TableCell>{rowUser.email}</TableCell>
                          <TableCell>
                            <Chip 
                              label={rowUser.role} 
                              color={rowUser.role === 'Admin' ? 'secondary' : 
                                    rowUser.role === 'Developer' ? 'primary' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={rowUser.isActive ? 'Ativo' : 'Inativo'}
                              color={rowUser.isActive ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {isAdminOrDeveloper && (
                              <Box sx={{ display: 'flex' }}>
                                <Tooltip title="Editar usuário">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleEditUser(rowUser)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir usuário">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleDeleteUser(rowUser)}
                                    disabled={user && user.id === rowUser.id}
                                  >
                                    <DeleteForever fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Nenhum usuário encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      </Container>
      
      {/* Dialog de edição de usuário */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Usuário: {selectedUser?.username}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome"
              name="firstName"
              value={editUserData.firstName}
              onChange={handleEditInputChange}
              fullWidth
              sx={darkModeTextField}
            />
            <TextField
              label="Sobrenome"
              name="lastName"
              value={editUserData.lastName}
              onChange={handleEditInputChange}
              fullWidth
              sx={darkModeTextField}
            />
            <TextField
              label="Email"
              name="email"
              value={editUserData.email}
              onChange={handleEditInputChange}
              fullWidth
              type="email"
              sx={darkModeTextField}
            />
            <FormControl fullWidth sx={darkModeSelect}>
              <InputLabel id="role-label">Função</InputLabel>
              <Select
                labelId="role-label"
                name="role"
                value={editUserData.role}
                label="Função"
                onChange={handleSelectChange}
              >
                <MenuItem value="Default">Usuário Padrão</MenuItem>
                <MenuItem value="Admin">Administrador</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={darkModeSelect}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                name="isActive"
                value={editUserData.isActive ? 'true' : 'false'}
                label="Status"
                onChange={handleSelectChange}
              >
                <MenuItem value="true">Ativo</MenuItem>
                <MenuItem value="false">Inativo</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancelar</Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#f44336' }}>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Você tem certeza que deseja excluir o usuário <strong>{selectedUser?.username}</strong>?
            </Typography>
            <Typography variant="body2" color="error">
              Esta ação não pode ser desfeita. Todos os dados do usuário serão removidos permanentemente.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button 
            onClick={handleConfirmDeleteUser} 
            variant="contained" 
            color="error"
            disabled={loading}
            startIcon={<DeleteForever />}
          >
            {loading ? <CircularProgress size={24} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Alertas de sucesso e erro */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="error">
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagementPage;